/** Small formatting helpers shared by the Audio Studio surfaces. */
import { useEffect, useState } from 'react';
import { libraryAudioUrl, libraryCoverUrl, libraryTrackKey, libraryTrackTitle, type LibraryTrack, type StudioTrack, type TrackSource, type Visibility } from '@/api/audioStudioApi';
import type { AttachableTrack } from './attachPayload';
import type { PlayableItem } from './AudioPlayer';

export function formatClock(s: number | null | undefined): string {
  if (s == null || !Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export function compactNumber(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(Math.round(n));
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function capitalize(s: string | null | undefined): string {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export const SOURCE_LABEL: Record<TrackSource, string> = {
  prompt: 'Prompt',
  preset: 'Preset',
  trend_lookalike: 'Trend lookalike',
  library: 'Library',
};

export const VISIBILITY_LABEL: Record<Visibility, string> = {
  private: 'Only me',
  members: 'Team members',
  team: 'All team',
};

export function studioPlayable(t: StudioTrack): PlayableItem {
  return {
    id: `studio:${t.track_id}`,
    title: t.title || 'Untitled',
    subtitle: [capitalize(t.genre), capitalize(t.mood)].filter(Boolean).join(' · ') || null,
    audioUrl: t.audio_url || null,
    coverUrl: t.cover_url,
  };
}

export function libraryPlayable(t: LibraryTrack): PlayableItem {
  return { id: `library:${libraryTrackKey(t)}`, title: libraryTrackTitle(t), subtitle: t.artist || null, audioUrl: libraryAudioUrl(t), coverUrl: libraryCoverUrl(t) };
}

/** Library tracks without a `track_id` cannot be attached (the contract keys attachments by id). */
export function libraryAttachable(t: LibraryTrack): AttachableTrack | null {
  return t.track_id ? { track_id: t.track_id, track_source: 'library', title: libraryTrackTitle(t) } : null;
}

export function studioAttachable(t: StudioTrack): AttachableTrack {
  return { track_id: t.track_id, track_source: t.source === 'library' ? 'library' : 'studio', title: t.title };
}

/** "just now", "2 min ago", "3 h ago", "yesterday", "4 days ago", else the date. */
export function formatRelative(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${Math.max(1, m)} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  if (d < 2) return 'yesterday';
  if (d < 7) return `${d} days ago`;
  return formatDate(iso);
}

/** What the sound is modelled on: "Like “<trend>”" for lookalikes, else where it came from ("Prompt", "Preset"…). */
export function trackOrigin(t: Pick<StudioTrack, 'source' | 'trend_ref'>): string {
  if (t.trend_ref?.title) return `Like “${t.trend_ref.title}”`;
  return SOURCE_LABEL[t.source] || capitalize(t.source);
}

/** One-line row subtitle: [owner ·] origin · genre · 0:30 · 2 min ago. */
export function trackSubtitle(t: StudioTrack, opts: { owner?: string | null; now?: number } = {}): string {
  return [
    opts.owner || null,
    trackOrigin(t),
    capitalize(t.genre) || null,
    t.duration_s ? formatClock(t.duration_s) : null,
    formatRelative(t.created_at, opts.now) || null,
  ].filter(Boolean).join(' · ');
}

/** Row subtitle when duration and date have their own columns: [owner ·] origin · genre. */
export function trackOrigins(t: StudioTrack, opts: { owner?: string | null } = {}): string {
  return [opts.owner || null, trackOrigin(t), capitalize(t.genre) || null].filter(Boolean).join(' · ');
}

/** The current time, refreshed every `everyMs` — keeps "2 min ago" labels honest without re-fetching. */
export function useNow(everyMs = 60_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
  return now;
}

/** Short "time ago" for meta lines: "just now", "5m ago", "3h ago", "2d ago", "3w ago", else the date. */
export function formatAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const m = Math.floor(Math.max(0, now - t) / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  if (d < 35) return `${Math.floor(d / 7)}w ago`;
  return formatDate(iso);
}

/** A list's total length: "45 s", "4 min 30 s", "1 h 5 min" (empty for 0). */
export function formatTotalDuration(totalS: number): string {
  const s = Math.round(totalS);
  if (!s || s < 0) return '';
  if (s < 60) return `${s} s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h) return m ? `${h} h ${m} min` : `${h} h`;
  const sec = s % 60;
  return sec ? `${m} min ${sec} s` : `${m} min`;
}

export const soundCount = (n: number) => `${n} sound${n === 1 ? '' : 's'}`;
